import React from "react";
import { useTranslation } from "react-i18next";
import { Send, X } from "lucide-react";
import { User } from "../types/Types";
import { RNRazmerDispatchRow, RNRazmerDispatchStage } from "../hooks/useRNWorkOrder";

interface Props {
  isOpen: boolean;
  stage?: RNRazmerDispatchStage;
  quantityLabel?: string;
  submitDisabledReason?: string;
  title?: string;
  workOrderNumber: string;
  senderName: string;
  rows: RNRazmerDispatchRow[];
  users: User[];
  selectedUserId: string;
  isSubmitting?: boolean;
  onUserChange: (userId: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}

const userLabel = (user: User): string => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return fullName || user.userName;
};

const RNRazmerDispatchModal: React.FC<Props> = ({
  isOpen,
  stage,
  title,
  quantityLabel,
  submitDisabledReason,
  workOrderNumber,
  senderName,
  rows,
  users,
  selectedUserId,
  isSubmitting = false,
  onUserChange,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const activeUsers = users.filter((user) => user.status !== "inactive");
  const allRowsReady = rows.length > 0 && rows.every(
    (row) => row.confirmed && row.verified && !!(row.measuredQty || "").trim()
  );
  const canSubmit = !!selectedUserId && allRowsReady && !isSubmitting && !submitDisabledReason;
  const heading = title || (stage ? t("rnDispatchModal." + stage) : "");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-4xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-gray-800">{heading}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50"
            aria-label={t("rnDispatchModal.close")}
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-2 border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs text-gray-700 sm:grid-cols-2">
          <div><span className="font-semibold">{t("rnDispatchModal.workOrder")}</span> {workOrderNumber || "-"}</div>
          <div><span className="font-semibold">{t("rnDispatchModal.sender")}</span> {senderName || "-"}</div>
        </div>

        <div className="max-h-[65vh] overflow-auto px-5 py-4">
          <table className="w-full border-collapse text-[11px]">
            <thead>
              <tr>
                <th className="border border-gray-300 bg-gray-200 px-2 py-1 text-center font-semibold text-gray-700">{t("rnDispatchModal.rowNumber")}</th>
                <th className="border border-gray-300 bg-gray-200 px-2 py-1 text-left font-semibold text-gray-700">{t("rnDispatchModal.ident")}</th>
                <th className="border border-gray-300 bg-gray-200 px-2 py-1 text-left font-semibold text-gray-700">{t("rnDispatchModal.name")}</th>
                <th className="border border-gray-300 bg-gray-200 px-2 py-1 text-right font-semibold text-gray-700">{quantityLabel || t("rnDispatchModal.measuredQty")}</th>
                <th className="border border-gray-300 bg-gray-200 px-2 py-1 text-center font-semibold text-gray-700">{t("rnDispatchModal.unit")}</th>
                <th className="border border-gray-300 bg-gray-200 px-2 py-1 text-left font-semibold text-gray-700">{t("rnDispatchModal.serial")}</th>
                <th className="border border-gray-300 bg-gray-200 px-2 py-1 text-left font-semibold text-gray-700">{t("rnDispatchModal.expiry")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.rowKey || index}>
                  <td className="border border-gray-300 px-2 py-1 text-center text-gray-700">{index + 1}</td>
                  <td className="border border-gray-300 px-2 py-1 text-gray-700">{row.ident}</td>
                  <td className="border border-gray-300 px-2 py-1 text-gray-700">{row.name}</td>
                  <td className={`border border-gray-300 px-2 py-1 text-right font-medium ${row.confirmed ? "text-gray-800" : "text-red-600"}`}>
                    {row.measuredQty || t("rnDispatchModal.notMeasured")}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-center text-gray-700">{row.unitCode}</td>
                  <td className="border border-gray-300 px-2 py-1 text-gray-700">{row.serialNumber || ""}</td>
                  <td className="border border-gray-300 px-2 py-1 text-gray-700">{row.expiryDate || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-end justify-between gap-4 border-t border-gray-200 px-5 py-4">
          <div className="w-full max-w-sm">
            <label className="mb-1 block text-xs font-semibold text-gray-700">{t("rnDispatchModal.recipient")}</label>
            <select
              value={selectedUserId}
              onChange={(event) => onUserChange(event.target.value)}
              className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-800 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50"
            >
              <option value="">{t("rnDispatchModal.selectRecipient")}</option>
              {activeUsers.map((user) => (
                <option key={user._id} value={user._id}>
                  {userLabel(user)}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            title={submitDisabledReason || (isSubmitting ? t("rnDispatchModal.sending") : !selectedUserId ? t("rnDispatchModal.selectRecipient") : !allRowsReady ? t("rnDispatchModal.notReady") : t("rnDispatchModal.send"))}
            className={`inline-flex h-9 items-center gap-2 rounded-md px-4 text-xs font-semibold transition-colors ${
              canSubmit
                ? "bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                : "cursor-not-allowed bg-gray-200 text-gray-400"
            }`}
          >
            <Send size={15} />
            {t("rnDispatchModal.send")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RNRazmerDispatchModal;
